import React from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Crown, Image as ImageIcon, Wand2, PlayCircle, StopCircle } from 'lucide-react';

const WATERMARK_POSITIONS = ['top_left', 'top_right', 'bottom_left', 'bottom_right'];
const WATERMARK_LABELS = { top_left: 'Top Left', top_right: 'Top Right', bottom_left: 'Bottom Left', bottom_right: 'Bottom Right' };

const SingleSlider = ({ value, onChange, min = 0, max = 100, step = 1, testId }) => (
  <div className="flex items-center gap-3">
    <Slider value={[value]} min={min} max={max} step={step} onValueChange={([v]) => onChange(v)} className="flex-1" data-testid={testId} />
    <span className="text-xs text-white w-10 text-right">{Math.round(value)}</span>
  </div>
);

/**
 * Main Editor "Brand" tab. Reuses the EXISTING Brand Kit records (creator +
 * app level, loaded once in UnifiedEditor.jsx) — never a second branding
 * system. Resolution order: creator's active Brand Kit → this project's
 * override (`brand_kit_id`) → per-item style overrides already supported
 * since Stage 3C. Watermark/intro/outro only ever reference the creator's
 * OWN real uploaded media/logo — nothing here invents an asset.
 */
export function EditorBrandDefaults({
  effectiveBrandKit, brandkits, branding, siblingSources,
  onSetBrandKitOverride, onToggleWatermark, onUpdateWatermark,
  onInsertIntro, onInsertOutro, onGenerateBrandPresets,
}) {
  const watermark = branding?.watermark || { enabled: false, logo_url: null, position: 'bottom_right', width_percent: 12, opacity_percent: 85 };
  const logoUrl = effectiveBrandKit?.logo_wordmark_url || effectiveBrandKit?.logo_symbol_url || null;

  return (
    <div className="space-y-4 pb-2" data-testid="brand-defaults-panel">
      <div className="p-2.5 rounded-lg bg-white/5 border border-white/10">
        {effectiveBrandKit ? (
          <div className="flex items-center gap-3">
            <div className="flex gap-1 shrink-0">
              <span className="w-5 h-5 rounded-full border border-white/20" style={{ background: effectiveBrandKit.primary_color }} />
              <span className="w-5 h-5 rounded-full border border-white/20" style={{ background: effectiveBrandKit.secondary_color }} />
            </div>
            <div className="min-w-0">
              <p className="text-xs text-white truncate flex items-center gap-1" data-testid="brand-defaults-active-kit">
                <Crown className="h-3 w-3 text-amber-400 shrink-0" />{effectiveBrandKit.name}
              </p>
              <p className="text-[10px] text-[#8A93A6] truncate">{effectiveBrandKit.font_family} · Applied to new text/captions automatically</p>
            </div>
          </div>
        ) : (
          <p className="text-xs text-[#8A93A6]" data-testid="brand-defaults-none">No Brand Kit yet — create one in your Creator dashboard to auto-apply colors/fonts here.</p>
        )}
      </div>

      <div className="space-y-1.5">
        <label className="text-xs text-[#8A93A6]">Project Brand Kit Override</label>
        <Select value={branding?.brand_kit_id || 'auto'} onValueChange={(v) => onSetBrandKitOverride(v === 'auto' ? null : v)}>
          <SelectTrigger className="h-8 bg-[#1C1F26] border-white/10 text-white text-sm" data-testid="brand-override-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="auto">Use creator's current default</SelectItem>
            {brandkits.map((bk) => <SelectItem key={bk.id} value={bk.id}>{bk.name}{bk.type === 'app' ? ' (App)' : ''}</SelectItem>)}
          </SelectContent>
        </Select>
        <p className="text-[10px] text-[#555E6E]">Pins this project to one kit so a later change to the creator's default won't move it.</p>
      </div>

      <div className="space-y-2 pt-2 border-t border-white/5">
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-1.5 text-xs text-[#8A93A6]"><ImageIcon className="h-3.5 w-3.5" />Logo Watermark</span>
          <Switch
            checked={!!watermark.enabled} disabled={!logoUrl}
            onCheckedChange={(v) => onToggleWatermark(v, logoUrl)}
            data-testid="brand-watermark-switch"
          />
        </div>
        {!logoUrl && <p className="text-[10px] text-[#555E6E]" data-testid="brand-watermark-no-logo">The active Brand Kit has no logo uploaded yet.</p>}
        {watermark.enabled && logoUrl && (
          <>
            <Select value={watermark.position} onValueChange={(v) => onUpdateWatermark({ position: v })}>
              <SelectTrigger className="h-8 bg-[#1C1F26] border-white/10 text-white text-sm" data-testid="brand-watermark-position-select"><SelectValue /></SelectTrigger>
              <SelectContent>{WATERMARK_POSITIONS.map((p) => <SelectItem key={p} value={p}>{WATERMARK_LABELS[p]}</SelectItem>)}</SelectContent>
            </Select>
            <label className="text-[10px] text-[#8A93A6]">Size</label>
            <SingleSlider value={watermark.width_percent} min={4} max={30} onChange={(v) => onUpdateWatermark({ width_percent: v })} testId="brand-watermark-size-slider" />
            <label className="text-[10px] text-[#8A93A6]">Opacity</label>
            <SingleSlider value={watermark.opacity_percent} onChange={(v) => onUpdateWatermark({ opacity_percent: v })} testId="brand-watermark-opacity-slider" />
          </>
        )}
      </div>

      <div className="space-y-2 pt-2 border-t border-white/5">
        <p className="text-xs text-[#555E6E] uppercase tracking-wide">Intro / Outro</p>
        <p className="text-[10px] text-[#555E6E]">Inserts one of your own uploaded sources at the very start/end of the Master timeline.</p>
        <div className="grid grid-cols-2 gap-2">
          <Select onValueChange={onInsertIntro}>
            <SelectTrigger className="h-8 bg-[#1C1F26] border-white/10 text-white text-xs" data-testid="brand-intro-select"><SelectValue placeholder="Insert intro…" /></SelectTrigger>
            <SelectContent>{siblingSources.map((s) => <SelectItem key={s.id} value={s.id}>{s.title || s.id}</SelectItem>)}</SelectContent>
          </Select>
          <Select onValueChange={onInsertOutro}>
            <SelectTrigger className="h-8 bg-[#1C1F26] border-white/10 text-white text-xs" data-testid="brand-outro-select"><SelectValue placeholder="Insert outro…" /></SelectTrigger>
            <SelectContent>{siblingSources.map((s) => <SelectItem key={s.id} value={s.id}>{s.title || s.id}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2 text-[10px] text-[#555E6E]">
          <PlayCircle className="h-3 w-3" />Intro shifts every existing clip later · <StopCircle className="h-3 w-3" />Outro appends after the last clip
        </div>
      </div>

      <div className="pt-2 border-t border-white/5">
        <Button size="sm" variant="outline" className="w-full border-white/10" disabled={!effectiveBrandKit} onClick={onGenerateBrandPresets} data-testid="brand-generate-presets-button">
          <Wand2 className="h-4 w-4 mr-2" />Generate Brand Text & Lower-Third Presets
        </Button>
        <p className="text-[10px] text-[#555E6E] mt-1">Adds "Brand Title" and "Brand Lower Third" style presets (using this kit's real colors/font) to the Text tab.</p>
      </div>
    </div>
  );
}
